import { X } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";

type ModalSize = "sm" | "md" | "lg";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: ModalSize;
}

const sizeClasses = {
  sm: "w-[90%] md:w-[70%] lg:w-[40%] max-w-md",
  md: "w-[90%] md:w-[70%] lg:w-[50%] max-w-lg",
  lg: "w-[90%] md:w-[70%] lg:w-[50%] max-w-2xl"
};

export function Modal({ isOpen, onClose, title, children, size = "md" }: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // SSR guard
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 md:p-6 lg:p-8 animate-[fadeIn_0.2s_ease-out]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div 
        className={`${sizeClasses[size]} rounded-2xl bg-slate-900/95 backdrop-blur-xl p-4 md:p-6 shadow-2xl border border-slate-700 max-h-[90vh] overflow-y-auto animate-[fadeInScale_0.25s_ease-out]`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          {title && (
            <h3 className="text-xl font-semibold text-emerald-100 pr-8">
              {title}
            </h3>
          )}
          <button
            onClick={onClose}
            className="ml-auto -mr-2 -mt-2 rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="text-slate-200">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
